
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import { fetchInProgressFiles, fetchSentencesForTranslator, fetchAssignedFileDetails } from "@store/thunks/translationThunk";
import { useNavigate } from "react-router-dom";
import { formatFileSize, ROLE_ROUTES, BASE_URL, getStatusStyle, languageIdToName, restrictSpecialCharacters } from "@utils/constants";
import { setSelectedAssignedFileId } from "@store/slices/translatorAdminSlice";
import { setFileAndLanguage } from "@store/slices/fileLanguageSlice";
import { setSelectedFileStatus } from "@store/slices/translatorSlice";
import { setFileMeta } from "@/store/slices/fileMetaSlice";

const FileDetailTableTranslator = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalFileId, setModalFileId] = useState(null);
  const { role, userId, token } = useSelector((store) => store.auth);
  const { assignedFileDetails = [], assignedFileDetailsLoading } = useSelector((store) => store.translatorAdmin);
  const { assignedFiles, selectedAssignedFolderId } = useSelector((store) => store.translatorAdmin);
  const selectedFolderId = useSelector((store) => store.translatorAdmin.selectedAssignedFolderId);
  const assignedFileArray = assignedFiles[selectedAssignedFolderId] || [];
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });

  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true); // 👈 track if more pages exist

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    if (restrictSpecialCharacters(newValue) || newValue === "") {
      setInputValue(newValue);
      setSearchQuery(newValue);
      setPageIndex(0);
      setHasNextPage(true); // Reset search → assume more pages exist
    } else {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Special characters are not allowed. Use only letters, numbers, and spaces.",
      });
    }
  };

  const handlePopupClose = () => {
    setPopup({ isOpen: false, type: "", message: "" });
  };


  // Function to fetch files
  const fetchFiles = async () => {
    if (selectedFolderId) {
      try {
        const res = await dispatch(fetchInProgressFiles(selectedFolderId, searchQuery, pageIndex, pageSize));
        if (Array.isArray(res)) {
          if (res.length < pageSize) {
            setHasNextPage(false);
          } else {
            setHasNextPage(true);
          }
        }
      } finally {
      }
    }
  };

  useEffect(() => {
    if (selectedFolderId) {
      fetchFiles();
    } else {
      navigate("/translator/folders");
    }
  }, [dispatch, searchQuery, selectedFolderId, pageIndex, pageSize, navigate]);


  useEffect(() => {
    dispatch(fetchInProgressFiles(selectedFolderId));
  }, [dispatch, selectedFolderId]);



  const handleFileClick = async ({ row }) => {
    setModalFileId(row.fileId);
    dispatch(setSelectedAssignedFileId(row.fileId));
    await dispatch(fetchAssignedFileDetails({
      fileId: row.fileId,
      translatorUserId: userId
    }));
    setIsModalOpen(true);
  };

  const handleOpenFile = async (detailArgument) => {
    if (detailArgument) {
      dispatch(setSelectedFileStatus(detailArgument.tStatus));
      dispatch(setFileAndLanguage({
        fileId: detailArgument.fileId,
        targetLanguageId: detailArgument.targetId,
        targetLanguageName: detailArgument.targetName,
      }));
      dispatch(setFileMeta({
        fileName: detailArgument.fileName,
      }))
      try {
        navigate("/translator/folders/files/sentences");
        setIsModalOpen(false);
      } catch (err) {
        alert("Error navigating to sentences page.");
        console.error("Error navigating:", err);
      }
    } else {
      console.log("No detail found for fileId", modalFileId, assignedFileDetails);
    }
  };

  const fileDetailColumns = [
    {
      id: "serial_number",
      header: () => "Sr. No.",
      cell: ({ row, table }) => {
        return pageIndex * pageSize + row.index + 1;
      },
      cellClassName: "text-center",
    },
    {
      id: "fileName",
      header: () => "File Name",
      accessorKey: "fileName",
      cell: ({ row }) => (
        <div className="max-w-md">{row.original.fileName || "N/A"}</div>
      ),
    },
    {
      id: "sourceName",
      header: () => "Source Language",
      accessorKey: "sourceName",
      cell: ({ row }) => (
        <div className="max-w-md">{row.original.sourceName || "N/A"}</div>
      ),
    },
    {
      id: "targetName",
      header: () => "Target Language",
      accessorKey: "targetName",
      cell: ({ row }) => (
        <div className="max-w-md">{row.original.targetName || "N/A"}</div>
      ),
    },
    {
      id: "totalSentences",
      header: () => "Total Sentences",
      accessorKey: "totalSentences",
      cellClassName: "text-center",
    },
    {
      id: "status",
      header: () => "Status",
      accessorKey: "tStatus",
      cell: ({ row }) => {
        const style = getStatusStyle(row.original.tStatus);
        return (
          <span className={`inline-flex items-center text-center px-2.5 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}>
            {style.label || "N/A"}
          </span>
        )
      },
      cellClassName: "text-center",
    },
    {
      id: "progress",
      header: () => "Progress",
      accessorKey: "percentageCompleted",
      cell: ({ row }) => (
        <div className="flex items-center justify-center">
          <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
            <div
              className="bg-[#ec7026] h-2 rounded-full"
              style={{ width: `${row.original.percentageCompleted}%` }}
            ></div>
          </div>
          <span className="text-sm text-gray-600 font-['Semantic/Strong']">{row.original.percentageCompleted}%</span>
        </div>
      ),
    },
    {
      id: "sentenceStatistics",
      header: () => "Sentence Statistics",
      cell: ({ row }) => (
        <div className="text-sm font-['Semantic/Strong']">
          <div className="flex justify-between mb-1">
            <span className="text-gray-600">Total:</span>
            <span className="font-medium">{row.original.totalSentences || 0}</span>
          </div>
          <div className="flex justify-between mb-1">
            <span className="text-gray-600">Translated:</span>
            <span className="font-medium">{row.original.translatedSentenceCount || 0}</span>
          </div>
          <div className="flex justify-between mb-1">
            <span className="text-gray-600">Rejected:</span>
            <span className="font-medium">{row.original.rejectedSentenceCount || 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Completed:</span>
            <span className="font-medium">{row.original.percentageCompleted || 0}%</span>
          </div>
        </div>
      ),
    },
    {
      id: "action",
      header: () => "Action",
      cell: ({ row }) => (
        <button
          onClick={() => handleOpenFile(row.original)}
          className="inline-flex items-center px-3 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
        >
          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
            />
          </svg>
          Open File
        </button>
      ),
      cellClassName: "text-center",
    },
  ];

  const renderFileDetailsModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-200">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-[calc(100vw-2rem)] sm:w-4/5 md:w-3/4 lg:w-2/3 max-w-7xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4 z-10">
          <p className="text-sm text-gray-500 font-['Semantic/Strong'] mt-1">
            View details for the selected file
          </p>
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          {assignedFileDetailsLoading ? (
            <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
              <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
              <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
              <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
            </div>
          ) : assignedFileDetails.length > 0 ? (
            <div className="w-full overflow-x-auto">
              <GenericTable
                data={assignedFileDetails}
                columns={fileDetailColumns}
                showPagination={false}
                emptyMessage="No file details available"
              />
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
              No file details available
            </div>
          )}
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-4 border-t border-gray-200 bg-white flex justify-end gap-2">
          <button
            onClick={() => setIsModalOpen(false)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            Close
          </button>
        </div>
      </div>
    </div>
  );

  const fileColumns = [
    {
      id: "serial_number",
      header: () => "Sr. No.",
      cell: ({ row, table }) => {
        const { pageIndex = 0, pageSize = 10 } = table.getState().pagination;
        return pageIndex * pageSize + row.index + 1;
      },
      cellClassName: "text-center",
    },
    {
      id: "fileName",
      header: () => "File Name",
      accessorKey: "fileName",
      cellClassName: "text-[#ec7026] hover:text-[#d85d1a] focus:underline font-['Semantic/Strong']",
      onClick: handleFileClick,
    },
    {
      id: "fileSize",
      header: () => "File Size",
      accessorKey: "fileSizeBytes",
      cell: ({ row }) => (
        <span>{formatFileSize(row.original.fileSizeBytes)}</span>
      ),
    },
    {
      id: "totalSentences",
      header: () => "Total Sentences",
      accessorKey: "totalSentences",
      cellClassName: "text-center",
    },
    {
      id: "status",
      header: () => "Status",
      accessorKey: "fileStatus",
      cell: ({ row })  => {
        const style = getStatusStyle(row.original.fileStatus);
        return (
          <span className={`inline-flex items-center text-center px-2.5 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}>
            {style.label || "N/A"}
          </span>
        )
      },
      cellClassName: "text-center",
    },
        {
          id: "uploadedAt",
          header: () => "Uploaded At",
          accessorKey: "uploadedAt",
          cell: ({ row }) => {
            const deadline = row.original?.uploadedAt || row.uploadedAt;
            if (!deadline) return "N/A";
            try {
              const date = new Date(deadline);
              const now = new Date();
              const isOverdue = date < now;
              const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
              return (
                <div>
                  <div
                    className={
                      isOverdue
                        ? "text-red-600"
                        : isApproaching
                          ? "text-amber-600"
                          : null
                    }
                  >
                    {date.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                  <div className="text-sm text-gray-500">
                    {date.toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "numeric",
                      hour12: true,
                    })}
                  </div>
                </div>
              );
            } catch (e) {
              return deadline;
            }
          },
          cellClassName: "text-center",
        },
  ];

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="lg:pr-8 lg:pt-8 flex justify-end items-center">
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search Files..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
            value={inputValue}
            onChange={handleInputChange}
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {popup.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
              {popup.type === "error" ? "Error" : "Info"}
            </h2>
            <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
              {popup.message}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handlePopupClose}
                className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="w-full overflow-x-auto">
            <GenericTable
              data={assignedFileArray}
              columns={fileColumns}
              showPagination={true}
                manualPagination={true}
                pageIndex={pageIndex}  
                pageSize={pageSize}  
                hasNextPage={hasNextPage}
                onPaginationChange={({ pageIndex, pageSize }) => {
                  setPageIndex(pageIndex);
                  setPageSize(pageSize);
                }}
              emptyMessage="No files available"
            />
          </div>
        </div>
        {isModalOpen && renderFileDetailsModal()}
      </div>
    </div>
  );
};

export default FileDetailTableTranslator;